import { useState } from 'react'
import { cn } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { parseHhMm, toHhMm, type TimeInputPreset } from './time-input-utils'

/**
 * The one time input for the whole app.
 *
 * Every time field in the product used to be one of three things: a free-text
 * `HH:MM` box (scanner/history filter slices, backtest node params), a native
 * `<input type="time">` (backtest dialogs and form-builder rows), or a pair of
 * hour/minute dropdowns (the replay picker). The free-text boxes silently
 * reverted to the old value when a user guessed the format wrong, and the
 * native inputs render per-browser and follow the *OS* 12h/24h setting, which
 * contradicted every value we actually store.
 *
 * This component is the dropdown pattern the replay picker already proved out,
 * lifted to `components/ui/` so there is exactly one of it. Invalid input is
 * unrepresentable: you pick from the hours that exist and the minutes that
 * exist, so `onChange` only ever emits a well-formed 24-hour `HH:mm` (or `''`
 * when `clearable` and the user clears it).
 *
 * Display is 24-hour everywhere, matching the stored values, the backtest
 * preset lists (`04:00` / `09:30` / `16:00`), and how traders read the session.
 *
 * Two variants share the same dropdown data model: `split` (default) renders
 * hour, minute, and presets as three independently bordered triggers. `compact`
 * renders them as one bordered pill (bare inner triggers, hairline divider
 * before the presets chevron) for tight node-inspector rows where a single
 * ~112px field reads better than three.
 */

const HOURS = Array.from({ length: 24 }, (_, h) => h)

/** Sentinel for the clear row. Radix forbids an empty-string SelectItem value. */
const CLEAR_VALUE = '__clear__'
const PLACEHOLDER = '--'

/**
 * Heights match the three densities already in use: `sm` for the backtest node
 * inspector rows (h-6), `md` for filter rows and sheets (h-7), `lg` for
 * dialogs (h-8).
 */
const SIZE_CLS = {
  sm: 'h-6',
  md: 'h-7',
  lg: 'h-8',
} as const

/**
 * Trigger chrome from MiniSelect in `chart-window-control.tsx`, the house
 * pattern for a compact dropdown: filled field, visible border, hover state,
 * chevron at full strength. Styled as a bare input these do not read as
 * openable at all.
 */
const TRIGGER_CLS =
  'w-[52px] shrink-0 items-center justify-between gap-1 rounded border border-input ' +
  'bg-background/55 px-1.5 text-xs tabular-nums text-foreground shadow-xs ' +
  'transition-colors hover:bg-hover ' +
  '[&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:shrink-0 [&>svg]:opacity-100 [&>svg]:text-muted-foreground'

export interface TimeInputProps {
  /** 24-hour `HH:mm`. Unparseable or empty renders as placeholders. */
  value: string | null | undefined
  /** Always receives a well-formed `HH:mm`, or `''` when cleared. */
  onChange: (value: string) => void
  disabled?: boolean
  /** Adds a third dropdown of named shortcuts that set both halves at once. */
  presets?: readonly TimeInputPreset[]
  /** Adds a `--` row to both lists; picking it emits `''`. */
  clearable?: boolean
  size?: keyof typeof SIZE_CLS
  /**
   * `split` (default): three independently bordered triggers. `compact`: one
   * bordered pill with bare inner triggers, for tight node-inspector rows.
   */
  variant?: 'split' | 'compact'
  /** Minute granularity. 1 (default) keeps the full 60-entry list. */
  minuteStep?: number
  /** Names the field; the dropdowns append ` hour` / ` minute` / ` presets`. */
  ariaLabel: string
  /**
   * Wrapper classes. The control sizes itself, so this is for layout and
   * per-surface chrome, not width.
   */
  className?: string
  /**
   * Forwarded to both `SelectContent`s. Needed only when the input sits inside
   * a popover: Radix portals the menu to `<body>` at the shared `z-50`, so
   * without a higher `zIndex` it opens *behind* the popover that spawned it.
   */
  contentStyle?: React.CSSProperties
}

export function TimeInput({
  value,
  onChange,
  disabled,
  presets,
  clearable,
  size = 'md',
  variant = 'split',
  minuteStep = 1,
  ariaLabel,
  className,
  contentStyle,
}: TimeInputProps) {
  const parsed = parseHhMm(value)
  // One open menu at a time. Each half is its own Radix Select, and inside a
  // canvas node the pointer-down that would normally dismiss the first menu is
  // swallowed before it reaches the dismiss layer, so hour + minute could both
  // sit open. Owning the open state here makes opening one close the others.
  const [openPart, setOpenPart] = useState<'hour' | 'minute' | 'presets' | null>(null)
  const openProps = (part: 'hour' | 'minute' | 'presets') => ({
    open: openPart === part,
    // Functional update: when the second trigger is clicked, Radix fires the
    // first menu's close and the second's open in the same event, so the two
    // updates must compose instead of the last one clobbering the first.
    onOpenChange: (next: boolean) =>
      setOpenPart((prev) => (next ? part : prev === part ? null : prev)),
  })

  const step = Math.max(1, Math.floor(minuteStep))
  const minutes = Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step)

  const handleHour = (next: string) => {
    if (next === CLEAR_VALUE) return onChange('')
    // A half-set field still emits a whole time: the unset half is 00, not a
    // silent no-op that leaves the user staring at an unchanged field.
    onChange(toHhMm(Number(next), parsed?.minute ?? 0))
  }

  const handleMinute = (next: string) => {
    if (next === CLEAR_VALUE) return onChange('')
    onChange(toHhMm(parsed?.hour ?? 0, Number(next)))
  }

  const isCompact = variant === 'compact'

  const wrapperCls = isCompact
    ? cn(
        SIZE_CLS[size],
        'inline-flex w-28 shrink-0 items-center rounded border border-input bg-background/55 shadow-xs',
        className,
      )
    : cn('flex w-fit items-center gap-0.5', className)

  const triggerCls = isCompact
    ? 'h-full flex-1 min-w-0 border-0 bg-transparent shadow-none rounded-none px-1 py-0 justify-center text-xs tabular-nums text-foreground hover:bg-hover [&>svg]:hidden'
    : cn(SIZE_CLS[size], TRIGGER_CLS)

  const presetsTriggerCls = isCompact
    ? 'h-full w-6 shrink-0 border-0 border-l border-input bg-transparent px-1 py-0 shadow-none rounded-none text-xs tabular-nums text-foreground hover:bg-hover [&>svg]:ml-0 [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:shrink-0 [&>svg]:opacity-100 [&>svg]:text-muted-foreground'
    : cn(SIZE_CLS[size], TRIGGER_CLS, 'w-6 shrink-0 px-1 [&>svg]:ml-0')

  return (
    <div className={wrapperCls} data-variant={isCompact ? 'compact' : undefined}>
      <Select
        value={parsed ? String(parsed.hour) : ''}
        onValueChange={handleHour}
        disabled={disabled}
        {...openProps('hour')}
      >
        <SelectTrigger aria-label={`${ariaLabel} hour`} className={triggerCls}>
          <SelectValue placeholder={PLACEHOLDER} />
        </SelectTrigger>
        <SelectContent style={contentStyle} className="min-w-16">
          {clearable && (
            <SelectItem value={CLEAR_VALUE} className="text-xs">
              {PLACEHOLDER}
            </SelectItem>
          )}
          {HOURS.map((h) => (
            <SelectItem key={h} value={String(h)} className="text-xs">
              {String(h).padStart(2, '0')}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span className="text-muted-foreground text-xs">:</span>

      <Select
        value={parsed ? String(parsed.minute) : ''}
        onValueChange={handleMinute}
        disabled={disabled}
        {...openProps('minute')}
      >
        <SelectTrigger aria-label={`${ariaLabel} minute`} className={triggerCls}>
          <SelectValue placeholder={PLACEHOLDER} />
        </SelectTrigger>
        <SelectContent style={contentStyle} className="min-w-16">
          {clearable && (
            <SelectItem value={CLEAR_VALUE} className="text-xs">
              {PLACEHOLDER}
            </SelectItem>
          )}
          {minutes.map((m) => (
            <SelectItem key={m} value={String(m)} className="text-xs">
              {String(m).padStart(2, '0')}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {presets && presets.length > 0 && (
        <Select value="" onValueChange={onChange} disabled={disabled} {...openProps('presets')}>
          <SelectTrigger aria-label={`${ariaLabel} presets`} className={presetsTriggerCls}>
            <SelectValue placeholder="" />
          </SelectTrigger>
          <SelectContent style={contentStyle} className="w-20 min-w-20">
            {presets.map((preset) => (
              <SelectItem
                key={preset.value}
                value={preset.value}
                className="justify-center px-0 text-center text-xs"
              >
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}
